import React, { useState, useEffect, useMemo } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listCourses } from "../../../utils/courseHelpers"
import {
  FILTER_TYPES,
  ACTIVITY_STATUS_OPTIONS,
  buildAudience,
  applyFilters,
  createSegment,
  updateSegment,
  deleteSegment,
  listSegments,
} from "../../../utils/segmentHelpers"

const newFilterRow = () => {
  const def = FILTER_TYPES[0]
  return { id: `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, type: def.key, operator: def.operators[0].value, value: "" }
}

const FilterRow = ({ filter, onChange, onRemove, courses, programs, cohorts }) => {
  const def = FILTER_TYPES.find((f) => f.key === filter.type)

  const handleTypeChange = (type) => {
    const nextDef = FILTER_TYPES.find((f) => f.key === type)
    onChange({ ...filter, type, operator: nextDef.operators[0].value, value: "" })
  }

  const renderValueInput = () => {
    if (def.valueType === "none") return null
    if (def.valueType === "number") {
      return (
        <Input type="number" placeholder="e.g. 50" value={filter.value} onChange={(e) => onChange({ ...filter, value: e?.target?.value })} className="w-28" />
      )
    }
    if (def.valueType === "date") {
      return <Input type="date" value={filter.value} onChange={(e) => onChange({ ...filter, value: e?.target?.value })} className="w-40" />
    }
    if (def.valueType === "course") {
      const options = [{ value: "", label: "Select a course..." }, ...(courses?.map((c) => ({ value: c?.courseId, label: c?.title })) || [])]
      return <Select options={options} value={filter.value} onChange={(v) => onChange({ ...filter, value: v })} />
    }
    if (def.valueType === "program") {
      const options = [{ value: "", label: "Select a program..." }, ...(programs?.map((p) => ({ value: p?.programId, label: p?.title })) || [])]
      return <Select options={options} value={filter.value} onChange={(v) => onChange({ ...filter, value: v })} />
    }
    if (def.valueType === "cohort") {
      const options = [{ value: "", label: "Select a cohort..." }, ...(cohorts?.map((c) => ({ value: c?.cohortId, label: c?.name })) || [])]
      return <Select options={options} value={filter.value} onChange={(v) => onChange({ ...filter, value: v })} />
    }
    if (def.valueType === "activityStatus") {
      return <Select options={ACTIVITY_STATUS_OPTIONS} value={filter.value} onChange={(v) => onChange({ ...filter, value: v })} />
    }
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-border bg-muted/30">
      <div className="w-40">
        <Select options={FILTER_TYPES.map((f) => ({ value: f.key, label: f.label }))} value={filter.type} onChange={handleTypeChange} />
      </div>
      <div className="w-44">
        <Select options={def.operators} value={filter.operator} onChange={(v) => onChange({ ...filter, operator: v })} />
      </div>
      {renderValueInput()}
      <Button variant="ghost" size="icon" iconName="X" iconSize={14} onClick={onRemove} className="ml-auto" />
    </div>
  )
}

const SegmentsPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [audience, setAudience] = useState({ learners: [], programs: [], orders: [], cohorts: [] })
  const [courses, setCourses] = useState([])
  const [segments, setSegments] = useState([])
  const [loading, setLoading] = useState(true)

  const [showBuilder, setShowBuilder] = useState(false)
  const [editingSegmentId, setEditingSegmentId] = useState(null)
  const [filters, setFilters] = useState([])
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState("")

  const [expandedSegmentId, setExpandedSegmentId] = useState(null)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const loadAll = async () => {
    setLoading(true)
    const [aud, { courses: courseList }, { segments: segmentList }] = await Promise.all([
      buildAudience(orgId),
      listCourses(orgId),
      listSegments(orgId),
    ])
    setAudience(aud)
    setCourses(courseList || [])
    setSegments(segmentList || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const matchedLearners = useMemo(() => applyFilters(audience.learners, filters, audience), [audience, filters])

  const openNewBuilder = () => {
    setEditingSegmentId(null)
    setFilters([])
    setName("")
    setDescription("")
    setFormError("")
    setShowBuilder(true)
  }

  const openEditBuilder = (segment) => {
    setEditingSegmentId(segment.segmentId)
    setFilters(segment.filters || [])
    setName(segment.name || "")
    setDescription(segment.description || "")
    setFormError("")
    setShowBuilder(true)
  }

  const handleAddFilter = () => setFilters((prev) => [...prev, newFilterRow()])
  const handleFilterChange = (id, next) => setFilters((prev) => prev.map((f) => (f.id === id ? next : f)))
  const handleRemoveFilter = (id) => setFilters((prev) => prev.filter((f) => f.id !== id))

  const handleSaveSegment = async () => {
    if (!name?.trim()) {
      setFormError("Segment name is required")
      return
    }
    setSaving(true)
    const { error } = editingSegmentId
      ? await updateSegment(orgId, editingSegmentId, { name, description, filters })
      : await createSegment({ orgId, name, description, filters })
    setSaving(false)
    if (error) {
      setFormError(error?.message || "Could not save segment")
      return
    }
    setShowBuilder(false)
    loadAll()
  }

  const handleDelete = async () => {
    await deleteSegment(orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadAll()
  }

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{segments?.length || 0} saved segment{segments?.length === 1 ? "" : "s"}</p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={openNewBuilder}>
          New Segment
        </Button>
      </div>

      {showBuilder && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Segment name" placeholder="e.g. At-risk learners in Bitcoin course" value={name} onChange={(e) => setName(e?.target?.value)} />
              <Input label="Description (optional)" value={description} onChange={(e) => setDescription(e?.target?.value)} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">Filters</p>
                <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddFilter}>
                  Add Filter
                </Button>
              </div>
              {filters?.length === 0 ? (
                <p className="text-xs text-muted-foreground">No filters yet — this segment will match every learner. Add a filter to narrow it down.</p>
              ) : (
                <div className="space-y-2">
                  {filters?.map((f) => (
                    <FilterRow
                      key={f.id}
                      filter={f}
                      onChange={(next) => handleFilterChange(f.id, next)}
                      onRemove={() => handleRemoveFilter(f.id)}
                      courses={courses}
                      programs={audience.programs}
                      cohorts={audience.cohorts}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-accent/10 border border-accent/20">
              <span className="text-sm text-foreground">Matches {matchedLearners?.length || 0} learner{matchedLearners?.length === 1 ? "" : "s"} right now</span>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}

            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setShowBuilder(false)}>Cancel</Button>
              <Button variant="default" fullWidth loading={saving} onClick={handleSaveSegment}>
                {editingSegmentId ? "Save Changes" : "Save Segment"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading segments...</div>
      ) : segments?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Filter" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No segments yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Build a dynamic audience from progress, course, cohort, and more — for targeted comms.</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={openNewBuilder}>New Segment</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {segments?.map((segment) => {
            const matched = applyFilters(audience.learners, segment.filters || [], audience)
            const isExpanded = expandedSegmentId === segment.segmentId
            return (
              <Card key={segment.segmentId} className="dark:bg-transparent">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-foreground text-sm">{segment.name}</h4>
                      {segment.description && <p className="text-xs text-muted-foreground mt-0.5">{segment.description}</p>}
                    </div>
                    <Badge variant="secondary" className="shrink-0">{matched?.length || 0} learners</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" iconName={isExpanded ? "ChevronUp" : "ChevronDown"} iconPosition="right" onClick={() => setExpandedSegmentId(isExpanded ? null : segment.segmentId)}>
                      {isExpanded ? "Hide Matches" : "View Matches"}
                    </Button>
                    <Button variant="ghost" size="sm" iconName="Pencil" iconPosition="left" onClick={() => openEditBuilder(segment)}>Edit</Button>
                    <Button variant="ghost" size="icon" iconName="Trash2" iconSize={14} onClick={() => setPendingDeleteId(segment.segmentId)} className="ml-auto" />
                  </div>
                  {isExpanded && (
                    <div className="divide-y divide-border border-t border-border pt-2">
                      {matched?.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">No learners currently match this segment.</p>
                      ) : (
                        matched?.map((l) => (
                          <div key={l.userId} className="py-2 flex items-center justify-between">
                            <div>
                              <p className="text-sm text-foreground">{l.name || "Unnamed learner"}</p>
                              <p className="text-xs text-muted-foreground">{l.email || l.userId}</p>
                            </div>
                            <span className="text-xs text-muted-foreground">{l.avgProgress}%</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this segment" onConfirm={handleDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default SegmentsPanel